<template>
  <v-container
    v-if="user"
    fluid
    pa-0
  >
    <template v-if="!init && loading">
      <v-row dense>
        <v-col cols="12">
          <template v-for="(item) in 5">
            <v-skeleton-loader
              :key="`loader-${item}`"
              type="list-item-avatar-three-line"
            />
          </template>
        </v-col>
      </v-row>
    </template>

    <template v-else-if="init && !totalItems">
      <v-row dense>
        <v-col
          class="text-center"
          cols="12"
        >
          <v-icon
            color="brand-primary"
            size="128"
          >
            mdi-cart
          </v-icon>

          <h2>{{ $t('Account.OrderHistory.NoOrders.Title') }}</h2>
          <h4 class="font-weight-regular">
            {{ $t('Account.OrderHistory.NoOrders.Subtitle') }}
          </h4>
        </v-col>
      </v-row>
    </template>

    <template v-else-if="init && totalItems">
      <v-row dense>
        <v-col cols="12">
          <v-list
            class="py-0"
            color="transparent"
            three-line
          >
            <template v-for="(item, index) in items">
              <v-list-item
                :key="`order-${item.OrderId}-${index}`"
                :disabled="ordersCompleted(getOrderStores(item), 'OrderStatusId')"
                class="mb-2 brand-beige-light"
                @click="gotoStatusPage(item)"
              >
                <v-list-item-action class="mr-2 mr-sm-4 mr-md-6 align-self-center">
                  <v-chip
                    class="brand-green-dark--text brand-text-title px-sm-2 px-1 mb-2 justify-center overflow-hidden"
                    color="brand-primary"
                    label
                    style="min-width: 100px; width: 100px;"
                  >
                    {{ getOrderCode(item) }}
                  </v-chip>

                  <v-chip
                    class="brand-green-dark--text brand-text-title px-sm-2 px-1"
                    color="brand-primary"
                    label
                    style="min-width: 100px; display: inline-grid;"
                  >
                    <v-icon
                      class="d-flex mr-2"
                      size="16"
                    >
                      {{ item.Payment === PaymentTypeEnum.CASH ? 'mdi-currency-eur' : 'mdi-credit-card' }}
                    </v-icon>

                    <div class="text-right fill-width">
                      {{ item.OrderTotal | currency }}
                    </div>
                  </v-chip>
                </v-list-item-action>

                <v-list-item-content class="brand-green-dark--text">
                  <v-list-item-title class="brand-text-title">
                    <template v-if="getOrderStores(item).length === 1 && getStoreById(getOrderStores(item)[0].StoreId)">
                      <v-tooltip
                        :color="getOrderStatusById(getOrderStores(item)[0].OrderStatusId)?.Color || ''"
                        :style="`color: ${getContrastColor(getOrderStatusById(getOrderStores(item)[0].OrderStatusId)?.Color || '', 168)}`"
                        open-delay="250"
                        top
                      >
                        <template #activator="{on}">
                          <v-badge
                            v-if="getOrderStores(item).length === 1 && getStoreById(getOrderStores(item)[0].StoreId)"
                            :color="getOrderStatusById(getOrderStores(item)[0].OrderStatusId)?.Color || ''"
                            class="mr-3"
                            overlap
                            v-on="on"
                          >
                            <template #badge>
                              <v-icon :color="getContrastColor(getOrderStatusById(getOrderStores(item)[0].OrderStatusId)?.Color || '', 168)">
                                {{ getOrderStatusById(getOrderStores(item)[0].OrderStatusId).Icon }}
                              </v-icon>
                            </template>

                            <v-avatar
                              size="42"
                              style="border: var(--v-brand-green-dark-base) solid 1px;"
                              v-on="on"
                            >
                              <img :src="storeLogoById(getOrderStores(item)[0].StoreId)">
                            </v-avatar>
                          </v-badge>
                        </template>

                        <span>{{ getOrderStatusById(getOrderStores(item)[0].OrderStatusId).Name }}</span>
                      </v-tooltip>

                      {{ item.Stores.length === 1 ? toLocaleUpperCase(storeNameById(getOrderStores(item)[0].StoreId)) : '' }}
                    </template>

                    <template v-if="getOrderStores(item).length > 1">
                      <template v-for="(store, idx) in getOrderStores(item)">
                        <v-tooltip
                          :key="`order-${item.OrderId}-${index}-store-${store.StoreId}-${idx}-tooltip`"
                          :color="getOrderStatusById(store.OrderStatusId)?.Color || ''"
                          :style="`color: ${getContrastColor(getOrderStatusById(store.OrderStatusId)?.Color || '', 168)}`"
                          open-delay="250"
                          top
                        >
                          <template #activator="{on}">
                            <v-badge
                              :key="`order-${item.OrderId}-${index}-store-${store.StoreId}-${idx}-badge`"
                              :color="getOrderStatusById(store.OrderStatusId)?.Color || ''"
                              class="mr-4"
                              overlap
                              v-on="on"
                            >
                              <template #badge>
                                <v-icon>{{ getOrderStatusById(store.OrderStatusId).Icon }}</v-icon>
                              </template>

                              <v-avatar
                                v-if="getStoreById(store.StoreId)"
                                :key="`order-${item.OrderId}-${index}-store-${store.StoreId}-${idx}`"
                                size="42"
                                style="border: var(--v-brand-green-dark-base) solid 1px;"
                                v-on="on"
                              >
                                <img :src="storeLogoById(store.StoreId)">
                              </v-avatar>
                            </v-badge>
                          </template>

                          <span>{{ getOrderStatusById(store.OrderStatusId).Name }}</span>
                        </v-tooltip>
                      </template>
                    </template>
                  </v-list-item-title>

                  <v-list-item-subtitle class="my-1">
                    {{ getStoreOrderItemsName(item) }}
                  </v-list-item-subtitle>

                  <v-list-item-subtitle class="brand-grey--text brand-text-small">
                    {{ item.DateAdded }} {{ item.TimeAdded }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col class="text-center">
          <v-pagination
            v-model="currentPage"
            :disabled="loading"
            :length="totalItems"
            :total-visible="7"
            navigation-color="brand-primary"
            navigation-text-color="brand-green-dark"
          />
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import AccountOrderHistoryMixin from '@/theme/default/views/account/AccountOrderHistory.js'

export default {
  name  : 'AccountOrderHistory',
  mixins: [AccountOrderHistoryMixin]
}
</script>

<style scoped src="@/theme/default/views/account/AccountOrderHistory.css"></style>
<style scoped>
:deep(.v-pagination__navigation) {
  box-shadow: unset;
}

:deep(.theme--light.v-pagination .v-pagination__item) {
  color: var(--v-brand-green-dark-base);
  background-color: var(--v-brand-green-light-base);
  box-shadow: unset;
}

:deep(.theme--light.v-pagination .v-pagination__item--active) {
  font-weight: bold;
  color: var(--v-brand-green-dark-base) !important;
  background-color: var(--v-brand-primary-base) !important;
}

/deep/ .theme--light.v-skeleton-loader .v-skeleton-loader__list-item-avatar-three-line {
  background-color: var(--v-brand-beige-light-base);
}
</style>
